import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center whitespace-nowrap rounded-lg text-base font-medium transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:pointer-events-none disabled:opacity-50 border no-underline",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-foreground text-background shadow-sm hover:bg-foreground/90",
        destructive:
          "border-transparent bg-red-900 text-white shadow-sm hover:bg-red-800 focus-visible:ring-red-800 dark:bg-red-900 dark:hover:bg-red-800",
        outline:
          "border-border bg-transparent text-foreground shadow-sm hover:bg-muted",
        secondary:
          "border-border bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost:
          "border-transparent bg-transparent text-foreground hover:bg-muted",
        link: "border-0 bg-transparent text-foreground underline underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3",
        lg: "h-10 rounded-md px-6",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
